import React, { useState } from "react";
import { FileUp, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import BaseModal from "@reusables/Modals/BaseModal";
import { ReactComponent as SuccessSVG } from "@assets/icons/ic_success.svg";
import { BaseCircularProgressWithContent } from "@reusables/BaseCircularProgressWithContent";
import { resetOperation, selectOperation } from "@redux/features/dfs/dfsSlice";
import { isDataTransferStateActive, isDataTransferStateFinished, selectProgressMean } from "@redux/features/dfs/types";
import { useAppDispatch, useAppSelector } from "@redux/hooks";

const operation = "price_reconversion";

export default function PriceReconversionIndicator(): JSX.Element {
    const { t } = useTranslation("", { keyPrefix: "products.allProducts.reconversion.reconversionIndicator.states" });

    const dispatch = useAppDispatch();

    const [isResultModalOpen, setIsResultModalOpen] = useState(false);

    const state = useAppSelector(selectOperation(operation));

    return (
        <>
            {
                // While ACTIVE, show a progress indicator
                isDataTransferStateActive(state) && (
                    <BaseCircularProgressWithContent
                        size={26}
                        {
                            ...state.progress && {
                                value: selectProgressMean(state) * 100,
                                variant: "indeterminate"
                            }
                        }
                        tooltip={t("inProgress")}
                    >
                        <FileUp size={12} />
                    </BaseCircularProgressWithContent>
                )
            }
            {
                // When FINISHED, show a final state (100% progress) and allow opening a modal for result
                isDataTransferStateFinished(state) && (
                    <>
                        <BaseCircularProgressWithContent
                            size={26}
                            variant="determinate"
                            value={100}
                            sx={{
                                color: state.has_errors ? "#E25C5C" : "#A6DD4C"
                            }}
                            onClick={() => setIsResultModalOpen(true)}
                            tooltip={state.has_errors
                                ? t("finishedWithErrors")
                                : t("finishedSuccessfully")}
                        >
                            <FileUp size={12} />
                        </BaseCircularProgressWithContent>

                        <BaseModal
                            isOpen={isResultModalOpen}
                            onClose={() => {
                                dispatch(resetOperation({ operation }));
                                setIsResultModalOpen(false);
                            }}
                            padding="40px"
                            position="center"
                        >
                            {
                                state.has_errors ? (
                                    <div className="flex items-center gap-4">
                                        <XCircle width={40} height={40} fill="#FF0000" color="#FFF" />
                                        <div className="flex flex-col items-start space-y-4">
                                            <div className="text-accent font-bold text-xl text-center">
                                                {t("error.title")}
                                            </div>
                                            <div className="text-accent text-lg">
                                                {t("error.message")}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-4">
                                        <SuccessSVG width={40} height={40} />
                                        <div className="text-accent font-bold text-xl text-center">
                                            {t("reconverted")}
                                        </div>
                                    </div>
                                )
                            }
                        </BaseModal>
                    </>
                )
            }
        </>
    );
}
